/* eslint-disable sonarjs/cognitive-complexity */
import { PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { GraphFact } from '@/components/Charts/types';
import { isNullish } from '@/utils/booleans';

type DateGroupingFormat = 'yyyy' | 'mm-yyyy';
type Fact = PlotReportFactElement | GraphFact;
type ProcessFactsDataProps = {
  facts: Fact[];
  groupByLabel?: string;
  filterByLabelName?: string;
  filterByLabelValue?: string;
  filterByLabels?: Record<string, string>[];
  countFromLabel?: string | null;
  dateGroupingFormat?: DateGroupingFormat;
  transposeCategoriesToRows?: boolean;
  keepZeroCount?: boolean;
};

export const processFactsData = ({
  facts,
  groupByLabel,
  countFromLabel,
  filterByLabelName,
  filterByLabelValue,
  transposeCategoriesToRows,
  filterByLabels,
  dateGroupingFormat = 'yyyy',
  keepZeroCount = true,
}: ProcessFactsDataProps) => {
  let chartData: { [key: string]: string | number; xAxisPropslabel: string }[] | Fact[] = facts;

  if (filterByLabelName || filterByLabelValue || !isNullish(filterByLabels) || !keepZeroCount) {
    chartData = getFilteredFacts(chartData, keepZeroCount, filterByLabelName, filterByLabelValue, filterByLabels);
  }

  if (groupByLabel) {
    chartData = groupFactsByLabel(chartData, groupByLabel, dateGroupingFormat, countFromLabel);
  }

  if (transposeCategoriesToRows) {
    return (chartData as { xAxisPropslabel: string; [key: string]: string | number }[]).flatMap((row) =>
      Object.entries(row)
        .filter(([key]) => key !== 'xAxisPropslabel')
        .map(([key, value]) => ({ xAxisPropslabel: key, count: value as number })),
    );
  }

  return chartData;
};

const getFilteredFacts = (
  facts: Fact[],
  keepZeroCount: boolean,
  filterByLabelName?: string,
  filterByLabelValue?: string,
  filterByLabels?: Record<string, string>[],
) => {
  const hasFilterByLabels = Array.isArray(filterByLabels);

  if (hasFilterByLabels) {
    return facts.filter((fact) => {
      if (filterByLabels.length === 0) {
        return Array.isArray(fact.labels) && fact.labels.length === 0;
      }

      const hasSameLength = filterByLabels.length === fact?.labels?.length;
      const hasLabels = fact.labels?.every((label) =>
        filterByLabels.some((labelFilter) => label.name === labelFilter?.name && label.value === labelFilter?.value),
      );
      return hasSameLength && hasLabels;
    });
  }

  return facts.filter((fact) => {
    return fact.labels?.some((label) => {
      if (filterByLabelName && filterByLabelValue) {
        return label.name === filterByLabelName && label.value === filterByLabelValue;
      }
      if (filterByLabelName) {
        return label.name === filterByLabelName;
      }
      if (filterByLabelValue) {
        return label.value === filterByLabelValue;
      }

      if (!keepZeroCount) {
        const value = typeof fact.value === 'string' ? parseFloat(fact.value) : fact.value;
        return value !== 0;
      }

      return false;
    });
  });
};

const getFormattedDate = (inputDate: string, dateGroupingFormat: DateGroupingFormat) => {
  const date = new Date(inputDate);
  const dateYear = date.getFullYear().toString();

  switch (dateGroupingFormat) {
    case 'mm-yyyy':
      return `${(date.getMonth() + 1).toString()}-${dateYear}`;
    case 'yyyy':
    default:
      return dateYear;
  }
};

const getFactDateByGroupingOrder = (fact: Fact, dateGroupingFormat: DateGroupingFormat) => {
  if (isNullish(fact.date)) {
    return null;
  }
  return getFormattedDate(fact.date as string, dateGroupingFormat);
};

const getSortedDatesSet = (facts: Fact[], dateGroupingFormat: DateGroupingFormat) => {
  return [
    ...new Set(
      facts.reduce((acc, entry) => {
        const factDate = getFactDateByGroupingOrder(entry, dateGroupingFormat);
        if (factDate) {
          acc.push(factDate);
        }
        return acc;
      }, [] as string[]),
    ),
  ].sort();
};

const groupFactsByLabel = (
  facts: Fact[],
  groupByLabel: string,
  dateGroupingFormat: DateGroupingFormat,
  countFromLabel?: string | null,
): { xAxisPropslabel: string; [key: string]: string | number }[] => {
  const sortedDates = getSortedDatesSet(facts, dateGroupingFormat);

  return sortedDates.map((date) => {
    const factsFilteredByDate = facts.filter((fact) => getFactDateByGroupingOrder(fact, dateGroupingFormat) === date);

    const categoriesData = factsFilteredByDate.reduce(
      (acc, cur) => {
        const labelGroup = cur.labels?.find((label) => label.name === groupByLabel)?.value;
        if (!labelGroup) {
          return acc;
        }

        if (countFromLabel) {
          const countLabelValue = cur.labels?.find((label) => label.name === countFromLabel)?.value as
            | string
            | number
            | undefined;
          if (!countLabelValue) {
            return acc;
          }
          const labelCountValue = typeof countLabelValue === 'string' ? parseInt(countLabelValue, 10) : countLabelValue;
          acc[`${labelGroup}`] = (acc[`${labelGroup}`] ?? 0) + labelCountValue;
          return acc;
        }

        // normal count = +fact.value for each fact within the same label group
        const value = typeof cur.value === 'string' ? (parseInt(cur.value, 10) ?? 0) : (cur.value as number);
        acc[`${labelGroup}`] = (acc[`${labelGroup}`] ?? 0) + value;
        return acc;
      },
      {} as { [key: string]: number },
    );

    return {
      xAxisPropslabel: date,
      ...categoriesData,
    };
  });
};
